import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  XMarkIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  ArrowDownIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

const ImageGalleryModal = ({ 
  isOpen, 
  images, 
  currentIndex = 0, 
  onClose, 
  onIndexChange,
  projectId = null
}) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadMenuRef = useRef(null);

  useEffect(() => {
    setActiveIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // 클릭 외부 감지로 다운로드 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // 터치 스와이프 핸들러
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeIndex < images.length - 1) {
      handleNext();
    }
    if (isRightSwipe && activeIndex > 0) {
      handlePrev();
    }
  };

  const handleNext = () => {
    if (activeIndex < images.length - 1) {
      const newIndex = activeIndex + 1;
      setActiveIndex(newIndex);
      onIndexChange && onIndexChange(newIndex);
    }
  };

  const handlePrev = () => {
    if (activeIndex > 0) {
      const newIndex = activeIndex - 1;
      setActiveIndex(newIndex);
      onIndexChange && onIndexChange(newIndex);
    }
  };

  const handleClose = () => {
    setIsZoomed(false);
    setScale(1);
    onClose();
  };

  const toggleZoom = () => {
    if (isZoomed) {
      setScale(1);
      setIsZoomed(false);
    } else {
      setScale(2);
      setIsZoomed(true);
    }
  };

  // 단일 이미지 다운로드
  const downloadSingleImage = async (imageUrl, fileName) => {
    try {
      setIsDownloading(true);
      
      // 모바일/WebView 체크
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
      
      if (isMobile && window.Android) {
        // Android WebView에서 처리 - 브라우저 기본 다운로드 사용
        toast.info('이미지 다운로드를 시작합니다...');
        
        // URL이 상대경로인 경우 절대경로로 변환
        const baseUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:5000' 
          : window.location.origin;
        const absoluteUrl = imageUrl.startsWith('http') 
          ? imageUrl 
          : `${baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
        
        // 다운로드 링크 생성 및 클릭 (WebView의 DownloadListener가 처리)
        const a = document.createElement('a');
        a.href = absoluteUrl;
        a.download = fileName || `image_${activeIndex + 1}.jpg`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        toast.success('다운로드가 시작되었습니다');
      } else {
        // PC 브라우저에서 처리
        const baseUrl = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:5000' 
          : '';
        const fetchUrl = imageUrl.startsWith('http') 
          ? imageUrl 
          : `${baseUrl}${imageUrl}`;
        const response = await fetch(fetchUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `image_${activeIndex + 1}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('이미지 다운로드 완료');
      }
    } catch (error) {
      console.error('다운로드 실패:', error);
      toast.error('다운로드 실패');
    } finally {
      setIsDownloading(false);
      setShowDownloadMenu(false);
    }
  };

  // 전체 이미지 ZIP 다운로드
  const downloadAllAsZip = async () => {
    try {
      setIsDownloading(true);
      
      // 모바일/WebView 체크
      const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
      
      if (isMobile && window.Android) {
        // Android WebView에서 처리 - 개별 다운로드로 진행
        toast.info(`${images.length}개 이미지 다운로드를 시작합니다...`);
        
        // 각 이미지를 개별적으로 다운로드
        images.forEach((imageUrl, index) => {
          // URL이 상대경로인 경우 절대경로로 변환
          const baseUrl = process.env.NODE_ENV === 'development' 
            ? 'http://localhost:5000' 
            : window.location.origin;
          const absoluteUrl = imageUrl.startsWith('http') 
            ? imageUrl 
            : `${baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
          
          const fileName = `image_${index + 1}.jpg`;
          
          // 다운로드 링크 생성 및 클릭 (WebView의 DownloadListener가 처리)
          setTimeout(() => {
            const a = document.createElement('a');
            a.href = absoluteUrl;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }, index * 500); // 각 다운로드 사이에 500ms 간격
        });
        
        toast.success('다운로드가 시작되었습니다');
      } else {
        // PC 브라우저에서 ZIP 처리
        const zip = new JSZip();
        const imgFolder = zip.folder('images');
        
        // 각 이미지를 ZIP에 추가
        const promises = images.map(async (imageUrl, index) => {
          try {
            const baseUrl = process.env.NODE_ENV === 'development' 
              ? 'http://localhost:5000' 
              : '';
            const fetchUrl = imageUrl.startsWith('http') 
              ? imageUrl 
              : `${baseUrl}${imageUrl}`;
            const response = await fetch(fetchUrl);
            const blob = await response.blob();
            const fileName = `image_${index + 1}.jpg`;
            imgFolder.file(fileName, blob);
          } catch (error) {
            console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
          }
        });

        await Promise.all(promises);
        
        // ZIP 파일 생성 및 다운로드
        const content = await zip.generateAsync({ type: 'blob' });
        const zipFileName = projectId 
          ? `project_${projectId}_images.zip`
          : `images_${new Date().getTime()}.zip`;
        saveAs(content, zipFileName);
        
        toast.success(`${images.length}개 이미지를 ZIP으로 다운로드했습니다`);
      }
    } catch (error) {
      console.error('ZIP 다운로드 실패:', error);
      toast.error('ZIP 다운로드 실패');
    } finally {
      setIsDownloading(false);
      setShowDownloadMenu(false);
    }
  };

  // 다운로드 버튼 클릭 핸들러
  const handleDownloadClick = () => {
    if (images.length === 1) {
      // 이미지가 1장만 있으면 바로 다운로드
      downloadSingleImage(images[0], 'image.jpg');
    } else {
      // 여러 장이면 메뉴 표시
      setShowDownloadMenu(!showDownloadMenu);
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black z-[10000]"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      {/* 헤더 */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent z-20 safe-top">
        <div className="flex items-center justify-between px-4 py-4" style={{ paddingTop: 'calc(env(safe-area-inset-top, 20px) + 1rem)' }}>
          <h3 className="text-white font-bold text-lg">
            사진 {images.length}장
          </h3>
          <div className="flex items-center gap-2">
            {/* 다운로드 버튼 */}
            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={handleDownloadClick}
                disabled={isDownloading}
                className="p-2 hover:bg-white/10 rounded-full transition-colors relative"
              >
                {isDownloading ? (
                  <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <ArrowDownTrayIcon className="h-6 w-6 text-white" />
                )}
              </button>
              
              {/* 다운로드 메뉴 */}
              {showDownloadMenu && images.length > 1 && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-30">
                  <button
                    onClick={() => downloadSingleImage(images[activeIndex], `image_${activeIndex + 1}.jpg`)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center gap-3"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 text-gray-600" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">현재 사진</div>
                      <div className="text-xs text-gray-500">image_{activeIndex + 1}.jpg</div>
                    </div>
                  </button>
                  <button
                    onClick={downloadAllAsZip}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors flex items-center gap-3 border-t"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">전체 다운로드</div>
                      <div className="text-xs text-gray-500">{images.length}장 (ZIP 파일)</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* 닫기 버튼 */}
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <XMarkIcon className="h-7 w-7 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* 메인 이미지 영역 */}
      <div className="relative h-full flex items-center justify-center">
        <div 
          className="relative w-full h-full flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* 이전 버튼 */}
          {activeIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute left-4 z-30 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <ChevronLeftIcon className="h-6 w-6 text-white" />
            </button>
          )}

          {/* 이미지 */}
          <img
            src={images[activeIndex]}
            alt={`이미지 ${activeIndex + 1}`}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `scale(${scale})`,
              transition: 'transform 0.3s ease-in-out'
            }}
            onClick={toggleZoom}
          />

          {/* 다음 버튼 */}
          {activeIndex < images.length - 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 z-30 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            >
              <ChevronRightIcon className="h-6 w-6 text-white" />
            </button>
          )}

          {/* 페이지 표시 */}
          <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">
              {activeIndex + 1} / {images.length}
            </span>
          </div>
        </div>
      </div>

      {/* 썸네일 리스트 */}
      {images.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent safe-bottom">
          <div className="px-4 pb-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 1rem)' }}>
            <div className="flex gap-2 overflow-x-auto py-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveIndex(index);
                    onIndexChange && onIndexChange(index);
                  }}
                  className={`flex-shrink-0 rounded-lg overflow-hidden transition-all ${
                    index === activeIndex 
                      ? 'ring-2 ring-white scale-105' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img}
                    alt={`썸네일 ${index + 1}`}
                    className="w-16 h-16 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* 닫기 버튼 */}
          <button
            onClick={handleClose}
            className="absolute bottom-8 right-4 bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors"
            style={{ marginBottom: 'env(safe-area-inset-bottom, 20px)' }}
          >
            <ArrowDownIcon className="h-4 w-4" />
            <span className="text-sm font-medium">닫기</span>
          </button>
        </div>
      )}
    </div>
  );

  // Portal을 사용하여 body에 직접 렌더링
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ImageGalleryModal;